import {
    useCallback, useMemo, useEffect, useState,
} from 'react'

import Collapse from '@mui/material/Collapse'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import RichTable from 'app/shared-components/RichTableReduxWrapper'
import useLoadFilters from 'app/Apps/DomainObject/hooks/useLoadFilters'

import useFirstLoading from 'app/hooks/useFirstLoading'
import useRenderTableControlPanel from './useRenderTableControlPanel'
import useCollapsablePanel from './hooks/useCollapsablePanel'
import useStyles from './CollapsibleTable.styles'
import {
    collapsibleDefaultTableProps,
    CollapsibleTableProps,
} from './CollapsibleTableProps'
import {
    CollapsibleTableContext,
} from './useCollapsibleTableContext'

export default function BaseCollapsibleTable({
    title,
    domainName,
    configName,
    columns,
    customUrl,
    requestHandlers,
    actionComponents: ActionComponents,
    createEnabled = true,
    tableControls,
    switchableFormTable = false,
    backToTableText,
    routeParams,
    radioSelect,
    preDefinedFilters,
    formId = undefined,
    formAction = undefined,
    updateFormId = undefined,
    updateFormParams = undefined,
    hideAllControls = false,
    autoOpenForm = true,
    ignorePreloading = false,
}: Readonly<CollapsibleTableProps & {
    formId?: string,
    formAction?: string,
    updateFormId?: ({
        id,
    }: { id: number | string }) => void,
    updateFormParams?: (params: {
        action?: string,
        id?: number | string
    }) => void,
}>) {
    const {
        classes,
    } = useStyles()

    const [
        search,
        setSearch,
    ] = useState<string>()

    const [
        id,
        setId,
    ] = useState<string | number | undefined>(formId)

    const [
        action,
        setAction,
    ] = useState<string | undefined>(formAction)

    const toggleFormId = useCallback(({
        id: newId,
        action: newAction,
    }: { id: number | string, action?: string }) => {
        if (action !== newAction && String(id) !== String(newId)) {
            setAction(newAction)
            setId(newId)
            return
        }
        if (action !== newAction) {
            setAction(newAction)
            return
        }
        if (String(id) !== String(newId)) {
            setId(newId)
            return
        }
        setId(undefined)
        setAction(undefined)
    }, [
        action,
        id,
        setId,
    ])

    const {
        handleIdChange,
        handleFormParamsChange,
    } = useMemo(() => {
        return {
            handleIdChange: updateFormId || toggleFormId,
            handleFormParamsChange: updateFormParams || toggleFormId,
        }
    }, [
        toggleFormId,
        updateFormId,
        updateFormParams,
    ])

    const {
        loadData, refetch, onParamsChange, data, isDataReady,
    } = requestHandlers

    const loaded = useFirstLoading(!isDataReady) || ignorePreloading

    useEffect(() => {
        onParamsChange?.({
            searchAll: search,
            filter: {},
        })
    }, [
        search,
        onParamsChange,
    ])

    useEffect(() => {
        setId(formId)
    }, [formId])

    useEffect(() => {
        setAction(formAction)
    }, [formAction])

    // effect to open the record if there's the only line in table
    useEffect(() => {
        if (loaded && autoOpenForm) {
            let result: any

            if (Array.isArray(data)) {
                result = data
            } else {
                result = data?.data
            }

            if (result?.length === 1) {
                handleIdChange({
                    id: result?.[0]?.id,
                })
            }
        }
    }, [loaded, autoOpenForm]) // eslint-disable-line

    const {
        openCreateForm,
        createFormOpened,
        closeCreateForm,
    } = useCollapsablePanel(String(formId))

    const renderTableControlPanel = useRenderTableControlPanel({
        title,
        stateName: domainName,
        createFormOpened,
        openCreateForm,
        closeCreateForm,
        createEnabled,
        tableControls,
        applySearch: setSearch,
        hideAllControls,
    })

    const injectLoadFilters = useLoadFilters({
        customUrl,
        preDefinedFilters,
        name: domainName,
    })

    const columnsWithFilters = useMemo(() => {
        return injectLoadFilters(columns)
    }, [
        injectLoadFilters,
        columns,
    ])

    const onCreate = useCallback(({
        id: newId,
    }) => {
        handleIdChange({
            id: newId,
        })
        refetch()
        closeCreateForm()
    }, [
        closeCreateForm,
        handleIdChange,
        refetch,
    ])

    const getUpdateForm = useCallback(() => {
        return (
            <div className={classes.detailsViewContainer}>
                <ActionComponents.Update
                    id={id}
                    refetch={refetch}
                />
            </div>
        )
    }, [
        ActionComponents,
        classes.detailsViewContainer,
        id,
        refetch,
    ])

    const renderCopyComponent = useCallback(() => {
        return ActionComponents.Copy ? (
            <ActionComponents.Copy
                id={id}
                refetch={refetch}
            />
        ) : null
    }, [
        id,
        refetch,
        ActionComponents,
    ])

    const renderCreateComponent = useCallback(() => {
        return ActionComponents.Create ? (
            <Collapse in={createFormOpened}>
                <ActionComponents.Create
                    onSuccess={onCreate}
                    onCancel={closeCreateForm}
                />
            </Collapse>
        ) : null
    }, [
        onCreate,
        closeCreateForm,
        ActionComponents,
        createFormOpened,
    ])

    const removeAllParams = useCallback(() => {
        handleIdChange({
            id: undefined,
        })
    }, [handleIdChange])

    const context = useMemo(() => {
        return {
            id: String(id),
            action,
            routeParams,
            updatePathWithId: handleIdChange,
            updatePathWithParams: handleFormParamsChange,
        }
    }, [
        id,
        action,
        routeParams,
        handleIdChange,
        handleFormParamsChange,
    ])

    return (
        <CollapsibleTableContext.Provider value={context}>
            <div className={classes.container}>
                {(switchableFormTable && id)
                    ? (
                        <>
                            <div className={classes.backToTableText}>
                                <ArrowBackIosIcon className={classes.icon} />
                                <a
                                    onKeyDown={removeAllParams} // eslint-disable-line max-len
                                    onClick={removeAllParams}
                                >
                                    {backToTableText}
                                </a>
                            </div>

                            {getUpdateForm()}
                        </>
                    ) : (
                        <>
                            {
                                action === 'copy'
                                    ? renderCopyComponent()
                                    : renderCreateComponent()
                            }
                            <RichTable
                                configName={configName || domainName}
                                name={domainName}
                                columns={customUrl ? columnsWithFilters : columns}
                                load={loadData}
                                onParamsChange={onParamsChange}
                                uniqField="id"
                                className={classes.tableContent}
                                classNames={{
                                    contentWrapper: classes.tableContentWrapper,
                                    headerWrapper: classes.tableHeaderWrapper,
                                }}
                                radioSelect={radioSelect}
                                detailPanel={switchableFormTable ? undefined : getUpdateForm}
                                onRowClick={handleIdChange}
                                openRowId={(action || createFormOpened)
                                    ? undefined
                                    : Number(id)}
                                renderControlPanel={renderTableControlPanel}
                            />
                        </>
                    )}
            </div>
        </CollapsibleTableContext.Provider>

    )
}

BaseCollapsibleTable.defaultProps = collapsibleDefaultTableProps
